import { useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useSessionStore } from '@/store/useSessionStore';
import { getPdfPageCount } from '@/utils/pdfPageCount';
import { getOfficePageCount } from '@/utils/officePageCount';
import { getApiErrorMessage } from '@/hooks/useApi';

const OFFICE_EXTENSIONS = ['docx', 'pptx', 'odt'];

/**
 * Stages a picked file locally for preview.
 * Page count, all in the browser:
 *   - image                 -> 1 page
 *   - PDF                   -> exact count via pdf.js
 *   - DOCX / PPTX / ODT     -> count stored inside the file
 *   - DOC / RTF / TXT / else, or if the file has no count -> `pending`,
 *     Print Options asks for a manual count
 */
export function useFileStaging() {
  const setUploadedFile = useSessionStore((s) => s.setUploadedFile);
  const setDocumentMeta = useSessionStore((s) => s.setDocumentMeta);
  const setPageInfo = useSessionStore((s) => s.setPageInfo);
  const setIsDetectingPageCount = useSessionStore((s) => s.setIsDetectingPageCount);

  // Ignores late results for a file that has since been replaced or removed
  const currentFileRef = useRef<File | null>(null);

  const detectPageCount = useCallback(
    async (file: File) => {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      const isImage = file.type.startsWith('image/');
      const isPdf = file.type === 'application/pdf' || ext === 'pdf';
      const isOffice = OFFICE_EXTENSIONS.includes(ext);

      if (isImage) {
        setPageInfo({ pageCount: 1, source: 'detected' });
        return;
      }

      if (!isPdf && !isOffice) {
        setPageInfo({ pageCount: null, source: 'pending' });
        return;
      }

      setIsDetectingPageCount(true);
      try {
        const pageCount = isPdf ? await getPdfPageCount(file) : await getOfficePageCount(file);
        if (currentFileRef.current !== file) return;

        if (pageCount === null) {
          // File has no stored count, so ask the user
          toast("Couldn't read this document's page count. Please enter it on the next step.", { icon: 'ℹ️' });
          setPageInfo({ pageCount: null, source: 'pending' });
        } else {
          setPageInfo({ pageCount, source: 'detected' });
        }
      } catch (error) {
        if (currentFileRef.current !== file) return;
        toast.error(
          getApiErrorMessage(error, "Couldn't read this file's page count. Please enter it manually."),
        );
        setPageInfo({ pageCount: null, source: 'pending' });
      } finally {
        if (currentFileRef.current === file) setIsDetectingPageCount(false);
      }
    },
    [setPageInfo, setIsDetectingPageCount],
  );

  const stageFile = useCallback(
    (file: File) => {
      currentFileRef.current = file;
      setUploadedFile(file);
      setDocumentMeta(null);
      setPageInfo({ pageCount: null, source: 'pending' });
      void detectPageCount(file);
    },
    [setUploadedFile, setDocumentMeta, setPageInfo, detectPageCount],
  );

  const clear = useCallback(() => {
    currentFileRef.current = null;
    setIsDetectingPageCount(false);
    setUploadedFile(null);
    setDocumentMeta(null);
    setPageInfo({ pageCount: null, source: 'pending' });
  }, [setUploadedFile, setDocumentMeta, setPageInfo, setIsDetectingPageCount]);

  return { stageFile, clear };
}